// The meter-category vocabulary (openspec: meter-categories). Mirrors
// backend/power_backend/consumption/meter_categories.py: keep the values, order and
// labels in step (lib/meterCategories.test.js reads the Python file to enforce it).
// Pure module: no React, no MUI, so node:test can import it directly.

export const NOT_CATEGORISED_LABEL = 'Not categorised'
export const UNSUBMETERED_MAIN_LABEL = 'Main incomer (not sub-metered)'

export const METER_CATEGORY_OPTIONS = [
    { value: 'main_incomer', label: 'Main incomer' },
    { value: 'sub_meter', label: 'Sub-meter' },
    { value: 'kitchen', label: 'Kitchen' },
    { value: 'refrigeration', label: 'Refrigeration' },
    { value: 'cellar_cooling', label: 'Cellar cooling' },
    { value: 'bedrooms', label: 'Bedrooms' },
    { value: 'plant_hvac', label: 'Plant / HVAC' },
    { value: 'lighting', label: 'Lighting' },
    { value: 'ev_charging', label: 'EV charging' },
    { value: 'generation', label: 'Generation' },
    { value: 'other', label: 'Other' }
]

// The physically sensible subset for gas meters.
export const GAS_CATEGORY_VALUES = [
    'main_incomer',
    'sub_meter',
    'kitchen',
    'bedrooms',
    'plant_hvac',
    'other'
]

// A fixed palette so a category keeps its colour across charts, peeks and the league.
// Chosen to stay distinguishable in light and dark themes; '' (uncategorised) is grey.
export const METER_CATEGORY_COLORS = {
    main_incomer: '#5B6C8F',
    sub_meter: '#8C6BB1',
    kitchen: '#E07B39',
    refrigeration: '#2A9DB5',
    cellar_cooling: '#3D7DD8',
    bedrooms: '#C2549D',
    plant_hvac: '#6E9F3A',
    lighting: '#D4A72C',
    ev_charging: '#1F9E89',
    generation: '#43A047',
    other: '#8D8D8D',
    '': '#B0B0B0'
}

const LABELS = Object.fromEntries(
    METER_CATEGORY_OPTIONS.map((option) => [option.value, option.label])
)

/** Display label for a stored category; blank reads "Not categorised". */
export function meterCategoryLabel(value) {
    if (!value) return NOT_CATEGORISED_LABEL
    return LABELS[value] ?? value
}

/** Label for a category bucket/series: a main incomer is its un-sub-metered remainder. */
export function breakdownLabel(value) {
    return value === 'main_incomer'
        ? UNSUBMETERED_MAIN_LABEL
        : meterCategoryLabel(value)
}

/** The category options valid for a utility ('gas' gets its subset; water has none). */
export function categoryOptionsFor(utility) {
    if (utility === 'gas') {
        return METER_CATEGORY_OPTIONS.filter((option) =>
            GAS_CATEGORY_VALUES.includes(option.value)
        )
    }
    if (utility === 'electricity') return METER_CATEGORY_OPTIONS
    return []
}

/** "Refrigeration, Cellar cooling" for a rule's category filter ('' when empty). */
export function formatCategoryList(values) {
    return (values ?? []).map(meterCategoryLabel).join(', ')
}

/** True when any meter in the list carries a category. */
export function orgHasCategorisedMeters(meters) {
    return (
        Array.isArray(meters) &&
        meters.some((meter) => Boolean(meter?.category))
    )
}

/** Colour for a category value, or for a series named by its label. */
export function meterCategoryColor(value) {
    return METER_CATEGORY_COLORS[value ?? ''] ?? METER_CATEGORY_COLORS.other
}

const VALUE_BY_SERIES_LABEL = Object.fromEntries([
    ...METER_CATEGORY_OPTIONS.map((option) => [option.label, option.value]),
    [UNSUBMETERED_MAIN_LABEL, 'main_incomer'],
    [NOT_CATEGORISED_LABEL, '']
])

/** `{seriesName: colour}` for category-grouped Explore series (named by label). */
export function categorySeriesColors(seriesNames) {
    return Object.fromEntries(
        (seriesNames ?? []).map((name) => [
            name,
            meterCategoryColor(VALUE_BY_SERIES_LABEL[name])
        ])
    )
}

/**
 * Stacked-bar segments for a property's `category_breakdown` (the estate portfolio row).
 *
 * Returns `{ segments, generation, showShares, total }`: consumption buckets with a
 * positive kWh, each `{category, label, kwh, sharePct, color}` with widths as a share of
 * consumption; the generation bucket (never part of the bar) separately; and
 * `showShares` false when sub-meters are not wired (`untreed_sub_meters`), where the UI
 * shows a wiring note instead of shares. `null` breakdown yields `null`.
 */
export function buildBreakdownSegments(breakdown) {
    if (!breakdown) return null
    const buckets = Array.isArray(breakdown.buckets) ? breakdown.buckets : []
    const consumption = buckets.filter(
        (bucket) => bucket.category !== 'generation' && Number(bucket.kwh) > 0
    )
    const total = consumption.reduce(
        (sum, bucket) => sum + Number(bucket.kwh),
        0
    )
    const segments = consumption.map((bucket) => ({
        category: bucket.category ?? '',
        label: bucket.label || breakdownLabel(bucket.category),
        kwh: Number(bucket.kwh),
        sharePct:
            bucket.share_pct ??
            (total ? Math.round((1000 * bucket.kwh) / total) / 10 : 0),
        color: meterCategoryColor(bucket.category)
    }))
    const generationBucket = buckets.find(
        (bucket) => bucket.category === 'generation'
    )
    return {
        segments,
        generation: generationBucket ? Number(generationBucket.kwh) : null,
        showShares: !breakdown.untreed_sub_meters,
        total
    }
}
